'use client';
import { useEffect, useRef } from 'react';
import posthog from 'posthog-js';

export interface ScreenTimeTrackingOptions {
  /**
   * Event name fired on mount. Receives `properties` as-is.
   */
  enterEvent: string;
  /**
   * Event name fired on unmount, tab close, or visibility change to hidden.
   * Receives `properties` plus `duration_seconds` (only active/visible time).
   */
  exitEvent: string;
  /**
   * Properties attached to both enter and exit events.
   */
  properties?: Record<string, any>;
  /**
   * Set to false to disable tracking entirely (e.g. while data is still loading).
   * Tracking starts on the first render where enabled becomes true.
   */
  enabled?: boolean;
  /**
   * Optional callback that returns additional properties to merge into the exit event.
   * Useful for reporting dynamic state captured during the screen visit (e.g. scroll depth).
   */
  getExitProperties?: () => Record<string, any>;
}

/**
 * Tracks the active, foreground time a user spends on a screen and fires enter/exit
 * PostHog events. The clock pauses when the tab is hidden so we don't count
 * background time as engagement.
 */
export const useScreenTimeTracking = ({
  enterEvent,
  exitEvent,
  properties,
  enabled = true,
  getExitProperties,
}: ScreenTimeTrackingOptions) => {
  const startedRef = useRef(false);
  const exitedRef = useRef(false);
  const activeStartRef = useRef<number>(0);
  const totalActiveMsRef = useRef(0);
  const isActiveRef = useRef(true);

  const exitEventRef = useRef(exitEvent);
  const propertiesRef = useRef(properties);
  const getExitPropertiesRef = useRef(getExitProperties);
  exitEventRef.current = exitEvent;
  propertiesRef.current = properties;
  getExitPropertiesRef.current = getExitProperties;

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    if (startedRef.current) return;
    startedRef.current = true;
    exitedRef.current = false;
    totalActiveMsRef.current = 0;
    isActiveRef.current = !document.hidden;
    activeStartRef.current = Date.now();

    posthog.capture(enterEvent, {
      ...properties,
      timestamp: Date.now(),
    });

    const captureExit = () => {
      if (exitedRef.current) return;
      exitedRef.current = true;

      let totalMs = totalActiveMsRef.current;
      if (isActiveRef.current) {
        totalMs += Date.now() - activeStartRef.current;
      }
      const durationSeconds = Math.round((totalMs / 1000) * 10) / 10;

      const extra = getExitPropertiesRef.current ? getExitPropertiesRef.current() : {};

      posthog.capture(exitEventRef.current, {
        ...propertiesRef.current,
        ...extra,
        duration_seconds: durationSeconds,
        timestamp: Date.now(),
      });
    };

    const handleVisibilityChange = () => {
      if (document.hidden && isActiveRef.current) {
        totalActiveMsRef.current += Date.now() - activeStartRef.current;
        isActiveRef.current = false;
      } else if (!document.hidden && !isActiveRef.current) {
        activeStartRef.current = Date.now();
        isActiveRef.current = true;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', captureExit);
    window.addEventListener('beforeunload', captureExit);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', captureExit);
      window.removeEventListener('beforeunload', captureExit);
      captureExit();
      startedRef.current = false;
    };
  }, [enabled, enterEvent]);
};
